import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, Rocket, Mail, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

// Sample onboarding steps (customize as needed)
export const onboardingSteps = [
  {
    id: 'org-info',
    title: 'Set Up Your Organization',
    description: 'Add your business name, logo, and contact details.',
    link: '/admin/settings',
  },
  {
    id: 'business-address',
    title: 'Add Your Business Address',
    description: 'Enter your address for invoices and search visibility.',
    link: '/admin/settings',
  },
  {
    id: 'invite-staff',
    title: 'Invite Staff/Admins',
    description: 'Add your team so they can help manage your center.',
    link: '/admin/staff',
  },
  {
    id: 'invite-parents',
    title: 'Invite Parents & Add Families',
    description: 'Add families and invite parents to the Parent Portal.',
    link: '/admin/families',
  },
  {
    id: 'create-class',
    title: 'Create Your First Class or Event',
    description: 'Add a class or event to start enrolling students.',
    link: '/admin/classes',
  },
  {
    id: 'connect-payments',
    title: 'Connect Payments (Stripe)',
    description: 'Enable online payments and invoicing for parents.',
    link: '/admin/settings',
  },
  {
    id: 'explore-parent-portal',
    title: 'Explore the Parent Portal',
    description: 'See what parents experience and manage their info.',
    link: '/admin/parent-portal-settings',
  },
  {
    id: 'customize-branding',
    title: 'Customize Your Branding',
    description: 'Adjust colors, logo, and appearance for your center.',
    link: '/admin/settings',
  },
];

const RELEASE_NOTES = [
  { date: '2024-06-01', title: 'Parent Portal Launched', description: 'A beautiful, mobile-friendly portal for parents.' },
  { date: '2024-05-20', title: 'Onboarding Checklist', description: 'Admins now see a helpful onboarding checklist.' },
  { date: '2024-05-10', title: 'Stripe Payments', description: 'Accept payments online with Stripe Connect.' },
  { date: '2024-04-30', title: 'Activity Feed', description: 'See all recent activity in your center.' },
];

const GettingStartedPage: React.FC = () => {
  const { user } = useAuth();
  const [completed, setCompleted] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'checklist' | 'releasenotes'>('checklist');
  const allComplete = completed.length === onboardingSteps.length;
  const [confettiFired, setConfettiFired] = useState(false);
  const navigate = useNavigate();

  // Fetch onboarding progress from Supabase
  useEffect(() => {
    if (!user?.id) return;
    const fetchProgress = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('onboarding_steps_completed')
        .eq('id', user.id)
        .single();
      if (error) {
        setError('Could not load onboarding progress.');
        setCompleted([]); // Defensive: clear completed if error
      } else if (data && typeof data === 'object' && 'onboarding_steps_completed' in data!) {
        setCompleted((data as { onboarding_steps_completed?: string[] }).onboarding_steps_completed || []);
      } else {
        setCompleted([]);
      }
      setLoading(false);
    };
    fetchProgress();
  }, [user?.id]);

  // Update onboarding progress in Supabase
  const handleGo = useCallback(async (stepId: string, link: string) => {
    if (!user?.id) return;
    const updated = completed.includes(stepId) ? completed : [...completed, stepId];
    setCompleted(updated);
    setLoading(true);
    const { error } = await supabase
      .from('user_profiles')
      .update({ onboarding_steps_completed: updated } as any)
      .eq('id', user.id);
    setLoading(false);
    if (error) {
      setError('Could not update onboarding progress.');
      return;
    }
    navigate(link);
  }, [completed, user?.id, navigate]);

  // Confetti when all steps complete
  useEffect(() => {
    if (allComplete && !confettiFired) {
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
      setConfettiFired(true);
    }
    if (!allComplete) setConfettiFired(false);
  }, [allComplete, confettiFired]);

  const progress = `${completed.length} of ${onboardingSteps.length} steps completed`;

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 w-full">
      <div className="flex flex-col items-center mb-8">
        <Rocket className="h-12 w-12 text-primary mb-2" />
        <h1 className="text-3xl font-bold text-center">Welcome to Hivetivity!</h1>
        <div className="text-muted-foreground text-center mt-2 text-lg">Let's get your activity center up and running in just a few steps.</div>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <Button size="sm" variant={tab === 'checklist' ? 'default' : 'outline'} onClick={() => setTab('checklist')}>Checklist</Button>
          <Button size="sm" variant={tab === 'releasenotes' ? 'default' : 'outline'} onClick={() => setTab('releasenotes')}>Release Notes</Button>
        </div>
        <span className="text-sm text-muted-foreground">{progress}</span>
      </div>
      {tab === 'checklist' && (
        <>
          {loading && <div className="text-center text-sm text-muted-foreground">Loading...</div>}
          {error && <div className="text-center text-sm text-red-500">{error}</div>}
          <ol className="space-y-4">
            {onboardingSteps.map((step, idx) => (
              <li key={step.id} className="flex items-start gap-3 bg-muted/50 rounded-lg p-4">
                <div className="flex-shrink-0 mt-1">
                  {completed.includes(step.id) ? (
                    <CheckCircle className="h-6 w-6 text-green-500" aria-label="Completed" />
                  ) : (
                    <span className="h-6 w-6 rounded-full border-2 border-primary flex items-center justify-center text-xs font-bold text-primary">{idx + 1}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-base">{step.title}</div>
                  <div className="text-sm text-muted-foreground mb-2">{step.description}</div>
                  <Button size="sm" variant={completed.includes(step.id) ? 'outline' : 'default'} onClick={() => handleGo(step.id, step.link)}>
                    {completed.includes(step.id) ? 'Go Again' : 'Go'}
                  </Button>
                </div>
              </li>
            ))}
          </ol>
          {allComplete && (
            <div className="flex items-center justify-center gap-2 mt-4 text-green-600 font-semibold">
              <Sparkles className="h-5 w-5" />
              All steps complete! You're ready to launch.
            </div>
          )}
        </>
      )}
      {tab === 'releasenotes' && (
        <div>
          <ul className="space-y-3">
            {RELEASE_NOTES.map(note => (
              <li key={note.date} className="bg-muted/50 rounded-lg p-3">
                <div className="font-semibold">{note.title} <span className="text-xs text-muted-foreground">({note.date})</span></div>
                <div className="text-sm text-muted-foreground">{note.description}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="pt-6 border-t flex flex-col items-center mt-8">
        <span className="mb-2 text-sm text-muted-foreground">Still have questions?</span>
        <Button size="lg" variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate('/contact')}>
          <Mail className="h-5 w-5 mr-2" />
          Contact Support
        </Button>
      </div>
    </div>
  );
};

export default GettingStartedPage; 